import React, { useEffect, useMemo, useState } from "react";
import Grid from "@material-ui/core/Grid";
import { useSortHandler } from "reactcoregk/hooks";
import { useDefaultTable } from "../../../helpers/hooks";
import BaseDialog from "../../../components/Common/BaseDialog";
import DefaultTable from "../../../components/Common/DefaultTable";
import Title from "../../../components/Common/Title";
import { ApiEndpoint } from "../../../store/@core/endpoint";
import { EntityType } from "../../../store/@core/entityType";
import { fetchData, formatDatesOnList, postData } from "../../../helpers/utils";
import { Paging } from "reactcoregk/models";
import Box from "@material-ui/core/Box";
import { Pagination } from "@material-ui/lab";
import SearchBox from "../../../components/Common/SearchBox";
import { makeStyles } from "@material-ui/styles";
import { FormControlLabel, Switch } from "@material-ui/core";
import { useCallback } from "react";
import { debounce } from "lodash";

const defaultCells = [
  {
    id: "cve",
    numeric: false,
    label: "CVE",
  },
  {
    id: "publishedDate",
    numeric: false,
    label: "Published At",
  },
  {
    id: "lastModifiedDate",
    numeric: false,
    label: "Last Update",
  },
];

const url = ApiEndpoint[EntityType.CveDefinition] + "/search";

const useStyles = makeStyles({
  dialogPaper: {
    minHeight: "calc(100% - 64px)",
    maxHeight: "calc(100% - 64px)",
  },
});

function ManageCvesDialog(props) {
  const classes = useStyles();

  const { attack, handleClose, open, callback } = props;

  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [cves, setCves] = useState(new Paging());
  const [errorMessage, setErrorMessage] = useState(null);
  const [query, setQuery] = useState("");
  const [showSelected, setShowSelected] = useState(false);
  const [initialCves, setInitialCves] = useState([]);

  const formatted = formatDatesOnList(
    cves.content,
    "LLL",
    "lastModifiedDate",
    "publishedDate"
  );

  const mapped = useMemo(() => {
    if (!formatted) return [];
    return formatted.map((row) => ({
      ...row,
      id: row.cve,
    }));
  }, [formatted, open]);

  const debouncedHandleSearch = useCallback(
    debounce((query) => {
      if (query) {
        setQuery(query);
      } else {
        setQuery("");
      }
    }, 500),
    []
  );

  const sortHandler = useSortHandler();
  const {
    renderHeadCell,
    renderCheckAllCell,
    renderDefaultBody,
    selected,
    setSelected,
  } = useDefaultTable(mapped, sortHandler);

  useEffect(() => {
    if (open) {
      setBusy(true);
      const APIURL = `${url}?page=${page}&size=20&s=${query}`;
      const payload = showSelected ? selected : [];
      postData(`${APIURL}`, payload)
        .then((data) => {
          setCves(data);
          setInitialCves(data);
          setBusy(false);
        })
        .catch((ex) => setBusy(false));
    }
  }, [open, page, query, showSelected]);

  useEffect(() => {
    if (open && attack && attack.cves && attack.cves[0]) {
      setSelected(attack.cves);
      setInitialCves(attack.cves);
    }
  }, [attack, open, setSelected]);

  const handleSave = () => {
    callback(selected);
    handleClose();
    setPage(0);
    setErrorMessage(null);
  };

  return (
    <BaseDialog
      title={"Manage CVES"}
      fullWidth={true}
      maxWidth={"lg"}
      primaryAction={handleSave}
      inProgress={busy}
      errorMessage={errorMessage}
      disableBackdropClick
      onClose={handleClose}
      {...props}
      classes={{ paper: classes.dialogPaper }}
    >
      <Grid container direction={"column"}>
        <Grid item>
          <Grid container>
            <Grid item style={{ flex: 1 }}>
              <Title>Select CVES to add</Title>
            </Grid>
            <Grid item style={{ display: "flex" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showSelected}
                    onChange={() => {
                      setShowSelected((s) => !s);
                      setPage(0);
                    }}
                    name="selected"
                    color="primary"
                  />
                }
                label="Show Selected"
              />
              <SearchBox
                placeholder={"cve, created or last update"}
                handleChange={(e) => {
                  debouncedHandleSearch(e.target.value);
                  setQuery(e.target.value);
                  setPage(0);
                }}
                query={query}
                syncQuery={(e) => {
                  setPage(0);
                  setQuery(e.target.value);
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        {!showSelected ? (
          <>
            <Grid item>
              <DefaultTable
                rows={mapped}
                isLoading={busy}
                headCells={defaultCells}
                renderHeadCell={renderHeadCell}
                renderCheckAllCell={renderCheckAllCell}
                renderDefaultBody={renderDefaultBody}
                selectableRows
              />
            </Grid>
            <Box mt={2} component={Grid} container justify={"center"}>
              <Pagination
                onChange={(e, newPage) => setPage(newPage - 1)}
                page={page + 1}
                count={cves.totalPages}
                showFirstButton
                showLastButton
                size={"small"}
              />
            </Box>
          </>
        ) : (
          <>
            <Grid item>
              <DefaultTable
                rows={mapped}
                isLoading={busy}
                headCells={defaultCells}
                renderHeadCell={renderHeadCell}
                renderCheckAllCell={renderCheckAllCell}
                renderDefaultBody={renderDefaultBody}
                selectableRows
              />
            </Grid>
            <Box mt={2} component={Grid} container justify={"center"}>
              <Pagination
                onChange={(e, newPage) => setPage(newPage - 1)}
                page={page + 1}
                count={initialCves.totalPages}
                showFirstButton
                showLastButton
                size={"small"}
              />
            </Box>
          </>
        )}
      </Grid>
    </BaseDialog>
  );
}

export default ManageCvesDialog;
